/**
 * Keeps one scope of the local file cache (a file tree's view: the project root, a service, brain…) in line with
 * vfs-service, cheaply: one listing call, then only what differs.
 *
 * - a cached file vfs-service no longer has disappears, unless it holds unsaved local work (NEW/MODIFIED);
 * - a file vfs-service has and the cache lacks is fetched (bytes kept exact for binaries);
 * - `refresh`: paths to fetch again even if cached (a file just overwritten elsewhere).
 *
 * This is what makes a delete, move or upload done in one tree (say a service's code view) show in another tree on
 * the same files (Home › Files at the project root), and the other way round.
 */
import * as vfsClient from './vfsClient';
import indexedDbFileStorage, { GIT_STATE } from './indexedDbFileStorage';
import { fullPathOf, isRootScope, relativeTo } from './cacheScopePaths';
import { bytesToBase64, isBinaryFile, mimeTypeFor } from './binaryFiles';

const inflight = new Map();
const lastRun = new Map();
/** A tree reloads often (every file event): a scope is listed again at most this often unless forced. */
const MIN_INTERVAL_MS = 1500;

const isDirty = (record) => {
  const state = record.gitState || GIT_STATE.MODIFIED;
  return state === GIT_STATE.NEW || state === GIT_STATE.MODIFIED;
};

async function mapLimit(items, limit, worker) {
  let next = 0;
  const run = async () => {
    while (next < items.length) {
      const index = next;
      next += 1;
      // eslint-disable-next-line no-await-in-loop
      await worker(items[index], index);
    }
  };
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, run));
}

/** Fetches one file from vfs-service into the cache under `scope` (binaries as base64, as uploads store them). */
export async function fetchIntoCache(projectId, scope, filePath) {
  const fullPath = fullPathOf(scope, filePath);
  if (isBinaryFile(filePath)) {
    const mimeType = mimeTypeFor(filePath);
    const blob = await vfsClient.readBinaryFile(projectId, fullPath, mimeType);
    if (!blob) { return false; }
    const content = bytesToBase64(new Uint8Array(await blob.arrayBuffer()));
    await indexedDbFileStorage.saveFile(projectId, scope, filePath, content, { fromVfs: true, isBinary: true, mimeType, size: blob.size, originalContent: content });
    return true;
  }
  const content = await vfsClient.readFile(projectId, fullPath);
  await indexedDbFileStorage.saveFile(projectId, scope, filePath, content, { fromVfs: true });
  return true;
}

async function reconcile(projectId, scope, refresh) {
  const listed = await vfsClient.listFilePaths(projectId, { path: isRootScope(scope) ? '.' : scope, depth: 50 });
  const onServer = new Set(listed.map((path) => relativeTo(scope, path)).filter(Boolean));
  const local = await indexedDbFileStorage.getAllFiles(projectId, scope);
  const localPaths = new Set(local.map((record) => record.filePath));
  const removed = [];
  for (const record of local) {
    // Never drop unsaved work, nor what the listing never shows (node_modules, dist… are not listed).
    if (onServer.has(record.filePath) || isDirty(record) || vfsClient.isSyncExcludedPath(fullPathOf(scope, record.filePath))) { continue; }
    // eslint-disable-next-line no-await-in-loop
    await indexedDbFileStorage.deleteFile(projectId, scope, record.filePath);
    removed.push(record.filePath);
  }
  const refreshable = new Set(refresh.filter((path) => onServer.has(path)));
  const missing = [...onServer].filter((path) => !localPaths.has(path) || refreshable.has(path));
  const added = [];
  await mapLimit(missing, 6, async (path) => {
    try {
      if (await fetchIntoCache(projectId, scope, path)) { added.push(path); }
    } catch (error) {
      console.warn('⚠️ Could not fetch', path, 'from vfs-service:', error); /* eslint-disable-line no-console */
    }
  });
  return { changed: removed.length > 0 || added.length > 0, removed, added };
}

/**
 * @param {string} projectId
 * @param {string} scope the tree's scope ('.', 'services/app', …)
 * @param {{force?: boolean, refresh?: string[]}} [options] force: list now even if just listed
 * @returns {Promise<{changed: boolean, removed: string[], added: string[]}>}
 */
export function reconcileScopeWithVfs(projectId, scope, { force = false, refresh = [] } = {}) {
  if (!projectId || !scope) { return Promise.resolve({ changed: false, removed: [], added: [] }); }
  const key = `${projectId}::${scope}`;
  const fresh = Date.now() - (lastRun.get(key) || 0) < MIN_INTERVAL_MS;
  if (inflight.has(key) && !refresh.length) { return inflight.get(key); }
  if (fresh && !force && !refresh.length) { return Promise.resolve({ changed: false, removed: [], added: [] }); }
  const run = (inflight.get(key) || Promise.resolve())
    .catch(() => null)
    .then(() => reconcile(projectId, scope, refresh))
    .finally(() => {
      lastRun.set(key, Date.now());
      if (inflight.get(key) === run) { inflight.delete(key); }
    });
  inflight.set(key, run);
  return run;
}

/** Tests only. */
export function resetReconcileState() {
  inflight.clear();
  lastRun.clear();
}
